import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import './_styles_/HolidayYearTableRow.css';
import {
    holidayDatesType,
    holidayDateType,
} from '../../types';
import useExtendedState from '../../hooks/useExtendedState';
import useSingleValidator from '../../hooks/useSingleValidator';
import {
    holidaysDateValidator,
    holidaysNameValidator,
} from '../../validators/holidays';
import { DAY_AND_MONTH } from '../fields/DateField';
import {
    toDateWithoutTime,
    toLocalDate,
} from '../../utils';
import useEffectOnUpdate from '../../hooks/useEffectOnUpdate';
import Privileges from '../../utils/Privileges';
import { Privileged } from '../../hocs/withPrivileges';
import PrivilegedTextField from '../fields/privileged/PrivilegedTextField';
import PrivilegedDateField from '../fields/privileged/PrivilegedDateField';
import { compositeValidator, wafValidator } from '../../hooks/validationSupport';

HolidayYearTableRow.propTypes = {
    holiday: holidayDateType.isRequired,
    holidays: holidayDatesType.isRequired,
    onHolidayYearDateDelete: PropTypes.func.isRequired,
    onHolidayYearDateChange: PropTypes.func.isRequired,
    year: PropTypes.number,
    doReset: PropTypes.bool.isRequired,
    doResetComponentsAfterModification: PropTypes.bool.isRequired,
};

HolidayYearTableRow.defaultProps = {
    year: null,
};

function HolidayYearTableRow(props) {
    const {
        year,
        holiday,
        holidays,
        onHolidayYearDateDelete,
        onHolidayYearDateChange,
        doReset,
        doResetComponentsAfterModification,
    } = props;
    const { t } = useTranslation();
    const nameState = useExtendedState(holiday.name);
    const dateWithoutYearState = useExtendedState(holiday.dateWithoutYear);
    const dateState = useExtendedState(toDateWithoutTime(`${year}-${holiday.dateWithoutYear}`));
    const {
        isValid: isNameValid,
        validator: nameValidator,
        reset: resetNameValidator,
    } = useSingleValidator(true, useMemo(() => compositeValidator(holidaysNameValidator(t), wafValidator(t)), [t]));
    const {
        isValid: isDateValid,
        validator: dateValidator,
        reset: resetDateValidator,
    } = useSingleValidator(true, useMemo(() => holidaysDateValidator(t, holidays, holiday.id), [t, holidays, holiday.id]));
    const onNameValidated = async (newName, isValid) => {
        const data = {
            id: holiday.id,
            name: newName,
            dateWithoutYear: dateWithoutYearState.value,
        };
        isDateValid()
            .then(() => onHolidayYearDateChange(data, isValid))
            .catch(() => onHolidayYearDateChange(data, false));
    };
    const onDateValidated = (newDate, isValid) => {
        let newDateWithoutYear = null;
        if (newDate && isValid) {
            const localDate = toLocalDate(newDate);
            if (localDate) {
                newDateWithoutYear = toLocalDate(newDate).substring(5);
            }
        }
        const data = {
            id: holiday.id,
            name: nameState.value,
            dateWithoutYear: newDateWithoutYear,
        };
        isNameValid()
            .then(() => onHolidayYearDateChange(data, isValid))
            .catch(() => onHolidayYearDateChange(data, false));

        if (newDateWithoutYear) {
            dateState.setValue(toDateWithoutTime(`${year}-${newDateWithoutYear}`));
        } else {
            dateState.setValue(newDate);
        }
        dateWithoutYearState.setValue(newDateWithoutYear);
    };
    const deleteClientApplication = () => {
        onHolidayYearDateDelete(holiday.id);
    };
    useEffectOnUpdate(() => {
        if (doReset) {
            nameState.reset();
            resetNameValidator();
            dateState.reset();
            resetDateValidator();
            dateWithoutYearState.reset();
        }
    }, [doReset]);
    useEffectOnUpdate(() => {
        if (doResetComponentsAfterModification) {
            nameState.changeInitialState(nameState.value);
            nameState.reset();
            resetNameValidator();
            dateState.changeInitialState(dateState.value);
            dateState.reset();
            resetDateValidator();
            dateWithoutYearState.changeInitialState(dateWithoutYearState.value);
            dateWithoutYearState.reset();
        }
    }, [doResetComponentsAfterModification]);
    const isValidDate = (currentDate) => currentDate.year() === year;
    return (
        <div className="detail-list-row holiday-row">
            <div className="holiday-year-cell--small holiday-year-table-row--remove">
                <Privileged sufficientPrivileges={[Privileges.HOLIDAY_EDIT]}>
                    <button
                        type="button"
                        className="delete-button button-small button-bordered"
                        aria-label={t('button.delete')}
                        onClick={deleteClientApplication}
                    >
                        <FontAwesomeIcon icon="times" />
                    </button>
                </Privileged>
            </div>
            <div className="holiday-year-cell holiday-year-table-row--name">
                <PrivilegedTextField
                    sufficientPrivileges={[Privileges.HOLIDAY_EDIT]}
                    state={nameState}
                    className="holiday-year-table-row--name--value table-input"
                    inputId="holiday-year-table-row--name--value"
                    onValidated={onNameValidated}
                    validator={nameValidator}
                    useDynamicWidth={false}
                />
            </div>
            <div className="holiday-year-cell holiday-year-table-row--date">
                <PrivilegedDateField
                    sufficientPrivileges={[Privileges.HOLIDAY_EDIT]}
                    state={dateState}
                    className="holiday-year-table-row--date--value table-input"
                    inputId="holiday-year-table-row--date--value"
                    validator={dateValidator}
                    onValidated={onDateValidated}
                    dateFormat={DAY_AND_MONTH}
                    isValidDate={isValidDate}
                />
            </div>
        </div>
    );
}

export default HolidayYearTableRow;
